import { environment } from '../../environments/environment';

export interface BlogCategoryOption {
  value: string;
  label: string;
}

export interface NavigationLink {
  label: string;
  path: string;
}

const BLOG_CATEGORY_OPTIONS: BlogCategoryOption[] = [
  { value: 'küchenkräuter', label: 'Küchenkräuter' },
  { value: 'gesundheit & wohlbefinden', label: 'Gesundheit & Wohlbefinden' },
  { value: 'gartentipps', label: 'Gartentipps' },
  { value: 'rezepte', label: 'Rezepte' },
  { value: 'tipps & tricks', label: 'Tipps & Tricks' },
];

const BLOG_CATEGORY_ALIASES: Record<string, string> = {
  // canonical/new
  kueche: 'küchenkräuter',
  kuechenkraeuter: 'küchenkräuter',
  gesundheit: 'gesundheit & wohlbefinden',
  gartentipps: 'gartentipps',
  rezepte: 'rezepte',
  tipps: 'tipps & tricks',
  tippsundtricks: 'tipps & tricks',
  tippsetricks: 'tipps & tricks',

  // legacy values
  kuechenkrauter: 'küchenkräuter',
  kuchenkrauter: 'küchenkräuter',
  heilkraeuter: 'gesundheit & wohlbefinden',
  kraeutermedizin: 'gesundheit & wohlbefinden',
  kraeuterwissen: 'tipps & tricks',
  kraeuteranbau: 'gartentipps',
  kraeuterpflege: 'gartentipps',
  wildkraeuter: 'gartentipps',
  kraeuterverarbeitung: 'tipps & tricks',
  kraeuterdiy: 'tipps & tricks',
  aromatischekraeuter: 'tipps & tricks',
  mediterranekraeuter: 'gartentipps',
  saisonalekraeuter: 'gartentipps',
  exotischekraeuter: 'gartentipps',
  kraeuterfueranfaenger: 'gartentipps',
  kraeutersammlungen: 'tipps & tricks',

  // english variants
  culinaryherbs: 'küchenkräuter',
  medicinalherbs: 'gesundheit & wohlbefinden',
  herbalmedicine: 'gesundheit & wohlbefinden',
  health: 'gesundheit & wohlbefinden',
  herbgrowing: 'gartentipps',
  herbcare: 'gartentipps',
  wildherbs: 'gartentipps',
  herbrecipes: 'rezepte',
  recipes: 'rezepte',
  herbknowledge: 'tipps & tricks',
  herbprocessing: 'tipps & tricks',
  herbdiy: 'tipps & tricks',
  guides: 'tipps & tricks',
};

const BLOG_CATEGORY_HEURISTICS: Record<string, string[]> = {
  rezepte: ['rezept', 'recipe', 'cook', 'kochen', 'zubereitung'],
  'küchenkräuter': ['kuechen', 'kuche', 'culinary', 'flavor', 'aroma', 'gewuerz', 'gewurz'],
  'gesundheit & wohlbefinden': ['gesund', 'heil', 'health', 'medic', 'wellness', 'symptom'],
  gartentipps: ['garten', 'anbau', 'grow', 'wild', 'pflege', 'plant'],
  'tipps & tricks': ['wissen', 'guide', 'diy', 'tipp', 'knowledge', 'howto'],
};

const INFO_CATEGORY_DISPLAY_NAMES: Record<string, string> = {
  kraeuter: 'Kraeuter',
  tees: 'Tees',
  gesundheit: 'Gesundheit',
};

const normalizedBaseUrl = String(environment.liveUrl || '').replace(/\/+$/, '');
const baseUrl = normalizedBaseUrl || 'https://example.com';
const baseUrlObj = (() => {
  try {
    return new URL(baseUrl);
  } catch {
    return new URL('https://example.com');
  }
})();

const defaultInfoCategorySlug = 'kraeuter';
const ROUTE_PATHS = {
  blogPath: '/blog',
  aboutPath: '/ueber-uns',
  impressumPath: '/impressum',
  datenschutzPath: '/datenschutz',
  seasonCalendarPath: '/kraeuter-saisonkalender',
  lexiconOverviewPath: '/kraeuter-lexikon',
} as const;

const DEFAULT_POPULAR_INFO_LINKS = [
  { label: 'Basilikum', slug: 'basilikum' },
  { label: 'Thymian', slug: 'thymian' },
  { label: 'Rosmarin', slug: 'rosmarin' },
  { label: 'Petersilie', slug: 'petersilie' },
  { label: 'Schnittlauch', slug: 'schnittlauch' },
] as const;

export const siteConfig = {
  brand: {
    displayName: 'Kräutersaat',
    websiteName: 'Kräutersaat',
    teamName: 'Kräutersaat Team',
    key: 'kraeutersaat',
  },
  locale: {
    code: 'de-DE',
    language: 'de',
  },
  urls: {
    baseUrl,
    blogBaseUrl: `${baseUrl}/blog`,
    blogSlugPrefix: `${baseUrlObj.hostname}/blog/`,
  },
  seo: {
    blogName: 'Kräutersaat Blog',
    defaultImageUrl: `${baseUrl}/api/images/optimized/kraeutersaat_logo?width=450&dpr=1.5`,
    organizationLogoUrl: `${baseUrl}/api/images/optimized/kraeutersaat_logo?width=450&dpr=1.5`,
    authorType: 'Organization' as const,
  },
  assets: {
    logoImageName: 'images/kraeutersaat_logo',
  },
  contact: {
    email: 'info@kraeutersaat.de',
  },
  legal: {
    companyName: 'Prism Corporation',
    representativeName: 'Andreas Felber',
    imprintEmail: 'info@kraeutersaat.de',
    siteHost: baseUrlObj.hostname,
  },
  routes: {
    ...ROUTE_PATHS,
  },
  content: {
    defaultInfoCategorySlug,
    infoCategoryDisplayNames: INFO_CATEGORY_DISPLAY_NAMES,
  },
  navigation: {
    headerLinks: [
      { label: 'Saisonkalender', path: ROUTE_PATHS.seasonCalendarPath },
      { label: 'Kräuter-Lexikon', path: ROUTE_PATHS.lexiconOverviewPath },
      { label: 'Blog', path: ROUTE_PATHS.blogPath },
      { label: 'Über uns', path: ROUTE_PATHS.aboutPath },
    ] as NavigationLink[],
    footerPopularInfoLinks: DEFAULT_POPULAR_INFO_LINKS.map((item) => ({
      label: item.label,
      path: `/${defaultInfoCategorySlug}/${item.slug}`,
    })) as NavigationLink[],
  },
  blog: {
    defaultCategory: 'tipps & tricks',
    categoryOptions: BLOG_CATEGORY_OPTIONS,
    categoryAliases: BLOG_CATEGORY_ALIASES,
    categoryHeuristics: BLOG_CATEGORY_HEURISTICS,
  },
} as const;

function normalizeKey(rawValue: string | null | undefined): string {
  return String(rawValue || '')
    .trim()
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/ß/g, 'ss')
    .replace(/&/g, ' und ')
    .replace(/[^a-z0-9]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function normalizePath(path: string | null | undefined): string {
  const raw = String(path || '').trim();
  if (!raw || raw === '/') return '/';
  return `/${raw.replace(/^\/+/, '').replace(/\/+$/, '')}`;
}

export function normalizeBlogCategory(
  rawValue: string | null | undefined,
  fallback: string = siteConfig.blog.defaultCategory
): string {
  const allowedValues = siteConfig.blog.categoryOptions.map((item) => item.value);
  const allowedLookup = new Map<string, string>(
    allowedValues
      .map((category) => [normalizeKey(category).replace(/\s+/g, ''), category] as const)
      .filter(([key]) => Boolean(key))
  );
  const normalizedFallbackKey = normalizeKey(fallback).replace(/\s+/g, '');
  const fallbackValue =
    allowedLookup.get(normalizedFallbackKey) ||
    siteConfig.blog.defaultCategory;
  const key = normalizeKey(rawValue);
  if (!key) return fallbackValue;

  const compact = key.replace(/\s+/g, '');
  const directMatch = allowedLookup.get(compact);
  if (directMatch) return directMatch;

  const mapped = siteConfig.blog.categoryAliases[compact];
  if (mapped) {
    const normalizedMapped = allowedLookup.get(normalizeKey(mapped).replace(/\s+/g, ''));
    if (normalizedMapped) return normalizedMapped;
  }

  for (const category of allowedValues) {
    const signals = siteConfig.blog.categoryHeuristics[category] || [];
    if (signals.some((signal) => compact.includes(normalizeKey(signal).replace(/\s+/g, '')))) {
      return category;
    }
  }

  return fallbackValue;
}

export function getBlogCategoryDisplayName(categoryValue: string | null | undefined): string {
  const normalized = normalizeBlogCategory(categoryValue, siteConfig.blog.defaultCategory);
  const match = siteConfig.blog.categoryOptions.find((item) => item.value === normalized);
  return match?.label || normalized;
}

export function getBlogCategoryOptions(): BlogCategoryOption[] {
  return [...siteConfig.blog.categoryOptions];
}

export function toRoutePath(path: string | null | undefined): string {
  return normalizePath(path).replace(/^\/+/, '');
}

export function buildSiteUrl(path: string | null | undefined = '/'): string {
  const normalizedPath = normalizePath(path);
  return normalizedPath === '/' ? siteConfig.urls.baseUrl : `${siteConfig.urls.baseUrl}${normalizedPath}`;
}

export function buildInfoDetailPath(
  slug: string | null | undefined,
  category: string | null | undefined = siteConfig.content.defaultInfoCategorySlug
): string {
  const safeSlug = String(slug || '').trim().replace(/^\/+/, '');
  const safeCategory = String(category || siteConfig.content.defaultInfoCategorySlug)
    .trim()
    .replace(/^\/+/, '');

  if (!safeSlug) return normalizePath(`/${safeCategory}`);
  return normalizePath(`/${safeCategory}/${safeSlug}`);
}

export function getInfoCategoryDisplayName(categorySlug: string | null | undefined): string {
  const key = String(categorySlug || '').trim().toLowerCase();
  return siteConfig.content.infoCategoryDisplayNames[key] || categorySlug || siteConfig.content.defaultInfoCategorySlug;
}
